"use client";

import React from "react";
import { motion } from "framer-motion";
import { Globe as Globe2, TriangleAlert as AlertTriangle, Layers, BookOpen, Compass } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
interface InsightItem {
  icon: React.ElementType;
  title: string;
  description: string;
  highlight: string;
}
const INSIGHTS: InsightItem[] = [{
  icon: Globe2,
  title: "Localisation Rather Than Mechanical Translation",
  description: "True localisation adapts terminology, search intent, currency, units of measurement, date formats, tone of voice, and regional payment and shipping details. Simply swapping strings leaves cultural nuances unaddressed and fails to satisfy native search expectations.",
  highlight: "Cultural adaptation drives qualified overseas conversions."
}, {
  icon: AlertTriangle,
  title: "Avoiding Copied Domestic Pages",
  description: "Copying UK or domestic pages into country folders with automated translation creates thin, generic experiences that search engines struggle to value. Modern search quality systems evaluate whether content satisfies user expectations in each specific locale.",
  highlight: "Avoid thin, automated clones across regional folders."
}, {
  icon: BookOpen,
  title: "Translator Briefs Driven by SEO Research",
  description: "Native speakers and professional translators deliver the best results when working from structured SEO briefs. I prepare clear keyword mappings, contextual intent notes, and regional guidance so copy reads naturally while capturing verified local search queries.",
  highlight: "Professional translators briefed with native keyword maps."
}, {
  icon: Layers,
  title: "Layout, Scripts, and Regulatory Context",
  description: "International content must accommodate script differences, right-to-left (RTL) text layouts where applicable, and regional legal standards such as local privacy disclosures and consumer rights notifications, ensuring a cohesive user experience.",
  highlight: "Accommodates script differences, RTL, and local regulations."
}];
export default function MultilingualLocalisation() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const
      }
    }
  };
  return <section id="multilingualseolocalisation" data-nav="light" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 border-b border-border selection:bg-accent/20">
      <div className="max-w-7xl mx-auto">
        {/* Editorial Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="max-w-3xl mb-14 md:mb-18">
          <Badge variant="secondary" className="mb-4 text-xs font-medium tracking-wide uppercase px-3 py-1 bg-secondary text-secondary-foreground rounded-full border border-border/60">
            International SEO Insights
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-default leading-tight mb-6">
            Multilingual SEO and Content Localisation
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            International search visibility requires far more than translating website copy word for word. Search
            engines and overseas buyers look for pages that genuinely reflect local language, currency, units,
            purchasing habits, and regulatory norms. Localisation aligns your offering with native search behaviour
            without producing thin, replicated content.
          </p>
        </motion.div>

        {/* Asymmetrical Content Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {INSIGHTS.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={item.title} variants={itemVariants} data-index={index}>
                <Card className="h-full bg-card/60 hover:bg-card border border-border hover:border-primary/40 shadow-xs transition-all duration-300 rounded-xl p-2 flex flex-col justify-between">
                  <CardHeader className="pb-4">
                    <div className="size-11 rounded-lg bg-secondary/80 text-primary flex items-center justify-center mb-4">
                      <Icon className="size-5" />
                    </div>
                    <CardTitle className="text-xl md:text-2xl font-semibold text-foreground font-default tracking-tight">
                      <h3 className="text-xl md:text-2xl font-semibold text-foreground font-default tracking-tight">
                        {item.title}
                      </h3>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-0">
                    <p className="text-base text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                    <div className="pt-3 border-t border-border/70 flex items-center gap-2">
                      <div className="size-1.5 rounded-full bg-accent" />
                      <span className="text-sm font-medium text-foreground">
                        {item.highlight}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}